import { Story } from '../../schema';
import {
  useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation,
  usePostStoriesByTripByTripIdStubMutation,
} from '../../store/backendApi';

interface UseStoryOperationsReturn {
  createStory: () => Promise<Story | null>;
  deleteStory: (storyId: string, confirmFn?: () => boolean) => Promise<void>;
  isCreatingStory: boolean;
  isDeletingStory: boolean;
}

/**
 * Hook to handle CRUD operations for stories.
 * Platform-agnostic: API calls work everywhere, confirmation is parameterized.
 *
 * @param tripId - The trip ID for story operations
 * @returns Story operation functions and loading states
 */
export const useStoryOperations = (tripId?: string): UseStoryOperationsReturn => {
  const [createStoryMutation, { isLoading: isCreatingStory }] =
    usePostStoriesByTripByTripIdStubMutation();

  const [deleteStoryMutation, { isLoading: isDeletingStory }] =
    useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation();

  const createStory = async (): Promise<Story | null> => {
    if (!tripId) return null;

    try {
      const stubStory = await createStoryMutation(tripId).unwrap();
      const newStory: Story = {
        ...stubStory,
        date: new Date().toISOString(),
        medias: [],
        title: '',
        tripId: tripId,
      };
      return newStory;
    } catch (error) {
      console.error('Failed to create story:', error);
      return null;
    }
  };

  const deleteStory = async (
    storyId: string,
    confirmFn: () => boolean = () => window.confirm('Are you sure you want to delete this story?'),
  ): Promise<void> => {
    if (!tripId) return;

    const confirmed = confirmFn();
    if (!confirmed) return;

    try {
      await deleteStoryMutation({ tripId, storyId });
    } catch (error) {
      console.error('Failed to delete story:', error);
    }
  };

  return {
    createStory,
    deleteStory,
    isCreatingStory,
    isDeletingStory,
  };
};
